import ImageEditor from "@Components/ImageEditor";
import GoogleMap from "@Components/Map";
import { optionsViewType } from "@Features/portal-settings";
import { PlusIcon } from "@Components/StickySidebarViewer/assets/selectionIcons";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import IconComponent from "@CubeComponents/icons";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import DateTimeComponent from "@CubeComponents/Mui_Form/DateTimeComponent";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { getDistanceInMeters, DEFAULT_IMAGE_DISPLAY_WIDTH } from "@Helpers/constants";
import { t } from "@i18n";
import { Box, CircularProgress, FormControlLabel, FormGroup, Link, RadioGroup } from "@mui/material";
import { convertDate } from "@Services/luxonHelper";
import TreeSelectDropDown from "@CubeComponents/html/TreeDropDown";
import { attachmentFileUrl } from "@Utils";
import _ from "lodash";
import { formSectionType, getUrlFromField } from "./constants";
import { getCalculatedFieldHintText, getConcatenateFieldValue, getFormulaFieldValue } from "./useQuestionFields";
import UserCard from "./userCard";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import Text from "@CubeComponents/html/Text/Text";
import { getViewerUrl } from "@Helpers/urlHelpers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import type { FormTemplateField, QuestionField, FormFileEntry } from "@ServerTypes/forms/forms.schema";
import type { UserTagProfileData } from "@ServerTypes/accounts/accounts.schema";
import type { ReactNode } from "react";
import type { TreeDropDownNode } from "@CubeComponents/html/TreeDropDown";

/**
 * A question as this renderer sees it — the stored/wire shape from `questionFieldSchema`, which
 * already models `displaySize` and the full `displayAs` set (including `dateAndTime`, which the
 * older hand-written type was missing).
 */
export type RenderableQuestionField = QuestionField & {
  /**
   * Persisted by the form-template builder (portal-settings FormsAndFields/FormTemplate) and read
   * by both the web and mobile renderers, but NOT modelled by `questionFieldSchema` — its
   * `.catchall(z.unknown())` would type them `unknown`, which no JSX position accepts. Declared
   * here, and reported as a server-schema gap; delete these two once the schema carries them.
   */
  requireAttachments?: boolean;
  fieldInstruction?: string;

  // KNOWN BUG, preserved verbatim: every `p.eachField.defaultValue` read below is dead. Nothing
  // writes `defaultValue` onto a question field — the only `defaultValue` in the form-template
  // builder (portal-settings FormsAndFields/FormTemplate/constants.tsx) is a per-property EDITOR
  // default ("text", "newTab") consumed by its own sidebar, never persisted onto the question — and
  // `questionFieldSchema` does not model it either. So the widgets' "template preset answer" always
  // falls through to `currentAnswer.value`. Typed `undefined` because that is what it always is; the
  // fix is to add `defaultValue` to `questionFieldSchema` and have the builder write it, at which
  // point this alias comes off and the reads become live. The reads that used it as the left-hand
  // side of `??` / `||` were statically dead branches and are gone; what is left are the two bare
  // pass-throughs to a widget's own `defaultValue` prop, which already receive `undefined` today.
  defaultValue?: undefined;
};

/* -------------------------------------------------------------------------- */
/* Answer value shapes, one per `fieldType`.                                   */
/*                                                                             */
/* `answerSchema.value` is `z.unknown()` on the server because the runtime      */
/* shape is chosen by the question's `fieldType`. Rather than leave that as     */
/* `any`, each shape is named here and each renderer declares the one it reads  */
/* (see `QuestionFieldRenderProps<TValue>`), so the renderer bodies need no     */
/* casts at all and a wrong read is a compile error.                           */
/* -------------------------------------------------------------------------- */

/**
 * A file / image entry stored in a `fileUpload` or `image` answer. `awsKey` is present for uploads
 * that went to S3 (the attachment endpoint streams them back by key); `fileVersion` for entries
 * picked out of the File Manager instead, which open through the viewer.
 *
 * Aliased from the server's `formFileEntrySchema` rather than re-declared, so this and the server's
 * `StoredAnswerFile` cannot drift: the server composes an entry on upload and echoes it back for this
 * list, which makes it one wire shape with one declaration.
 */
export type FieldFileValue = FormFileEntry;

/** A user entry stored in a `users` answer; `_id` is the account id the user card is keyed by. */
export type FieldUserValue = { _id: string } & Partial<UserTagProfileData>;

/** An entry stored in a `look-up` answer — a project Location node or a Team. */
export type FieldLookUpValue = { id: string; name: string };

/** The value stored by a `url` answer. */
export type FieldUrlValue = { URL?: string; displayText?: string };

/** A geo point as the answer stores it and `GoogleMap` consumes it. */
export type FieldGeoPoint = { lat: number; lng: number };

/** The value stored by a `geoLocation` answer. */
export type FieldGeoValue = { current?: FieldGeoPoint; selected?: FieldGeoPoint; mapZoomLevel?: number };

/**
 * The answer shape each `fieldType` stores, written down once.
 *
 * `answerSchema.value` is `z.unknown()` on the server (forms.schema.ts) because the shape is chosen
 * by the question's `fieldType`, and nothing in the type system ties the two together. This map is
 * that tie, and `toTypedFieldAnswer` below is where the tie is CHECKED — nothing here is asserted:
 * the read-only view narrows the union that function returns, so a branch that reads the wrong shape
 * for its `fieldType` is a compile error, and a stored value that is not the shape its `fieldType`
 * implies is reported as "no answer" instead of being renamed into one.
 *
 * The printable scalars are `string | number` rather than one or the other, because what is stored
 * differs by write path: `getAnswer` (questionFields.tsx) saves the raw DOM string for `single-line`
 * / `multi-line` / `single-select` and a `Number(…)` for `number` / `date-time`, and rows written by
 * the mobile app or by older builds carry the other one. Every read-only branch either prints the
 * value or runs it through `Number()`, so both are equally usable there.
 */
type AnswerValueByFieldType = {
  "single-line": string | number;
  "multi-line": string | number;
  "single-select": string | number;
  "multi-select": string[];
  "date-time": string | number;
  "look-up": FieldLookUpValue[];
  number: string | number;
  autoNumber: string | number;
  users: FieldUserValue[];
  fileUpload: FieldFileValue[];
  image: FieldFileValue[];
  url: FieldUrlValue;
  geoLocation: FieldGeoValue;
  formula: string | number;
  concatenate: string | number;
};

/**
 * One question's `fieldType` paired with the answer value that `fieldType` implies. `value` is
 * required-but-`undefined`able rather than optional so that a question whose answer failed the check
 * below is still a member of this union; an unanswered field genuinely stores nothing.
 *
 * The trailing member is the rest of `fieldTypeEnum` — `text`, `tableDataSource` and the four
 * task-form field types, none of which the renderers or the read-only view draw. Without it the
 * last branch of `getFieldAns` would have narrowed to the only member left and the fall-through
 * `return ""` would have looked unreachable, when at runtime it is exactly what those six reach.
 */
type TypedFieldAnswer =
  | {
      [K in keyof AnswerValueByFieldType]: { fieldType: K; value: AnswerValueByFieldType[K] | undefined };
    }[keyof AnswerValueByFieldType]
  | { fieldType: Exclude<QuestionField["fieldType"], keyof AnswerValueByFieldType>; value: unknown };

/* -------------------------------------------------------------------------- */
/* Runtime checks for the shapes above.                                        */
/*                                                                             */
/* Nothing upstream has looked at an answer by the time it reaches this file:   */
/* `answerSchema.value` is `z.unknown()`, so these predicates are the only      */
/* place the stored shape is established. Each checks exactly what the type it  */
/* names requires — no more, so a well-formed answer is never rejected, and no  */
/* less, so the name it hands out is one the value has earned.                  */
/* -------------------------------------------------------------------------- */

const isUnknownArray = (value: unknown): value is unknown[] => Array.isArray(value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !isUnknownArray(value);

const isPrintableScalar = (value: unknown): value is string | number =>
  typeof value === "string" || typeof value === "number";

const readString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

const readNumber = (value: unknown): number | undefined => (typeof value === "number" ? value : undefined);

/**
 * A `multi-select` answer. Entries are stringified rather than required to be strings, because the
 * only thing done with the list is `join(",")` and that is exactly what `Array.prototype.join` does
 * to a non-string entry — a stored list of `[null, null, "testOptionTwo"]` (there are such rows)
 * still renders ",,testOptionTwo" and does not lose the one real option in it.
 */
const readStringList = (value: unknown): string[] | undefined =>
  isUnknownArray(value) ? value.map((entry) => (entry == null ? "" : String(entry))) : undefined;

/**
 * A `look-up` answer — the project Location nodes or the Teams the picker stored. Entries missing a
 * `name` are dropped rather than failing the whole list: the oldest Location answers stored the id
 * on its own (see `LocationTreeSelect`), and dropping just those keeps the names of the rest, which
 * is the text actually in front of the reader.
 */
const readLookUpList = (value: unknown): FieldLookUpValue[] | undefined =>
  isUnknownArray(value)
    ? value.flatMap((entry) =>
        isRecord(entry) && typeof entry.id === "string" && typeof entry.name === "string"
          ? [{ id: entry.id, name: entry.name }]
          : [],
      )
    : undefined;

/**
 * A `users` answer. `_id` is the only member checked because it is the only one `FieldUserValue`
 * requires and the only one this file reads; the rest are the profile fields the user card already
 * takes as optional.
 */
const isUserEntry = (entry: unknown): entry is { _id: string } => isRecord(entry) && typeof entry._id === "string";

const readUserList = (value: unknown): Array<{ _id: string }> | undefined =>
  isUnknownArray(value) ? value.filter(isUserEntry) : undefined;

/**
 * A `fileUpload` / `image` answer. Every member of `formFileEntrySchema` is optional — an uploaded
 * file carries `awsKey`, a File-Manager pick carries `fileVersion` — so each is read as such rather
 * than demanded.
 */
const readFileList = (value: unknown): FieldFileValue[] | undefined =>
  isUnknownArray(value)
    ? value.flatMap((entry) =>
        isRecord(entry)
          ? [
              {
                awsKey: readString(entry.awsKey),
                name: readString(entry.name),
                type: readString(entry.type),
                fileVersion: readString(entry.fileVersion),
                fileId: readString(entry.fileId),
                resourceType: readString(entry.resourceType),
                versionNumber: readNumber(entry.versionNumber),
              },
            ]
          : [],
      )
    : undefined;

/** A `url` answer. Both members are optional on `FieldUrlValue`, so both are read as such. */
const readUrlValue = (value: unknown): FieldUrlValue | undefined =>
  isRecord(value) ? { URL: readString(value.URL), displayText: readString(value.displayText) } : undefined;

const readGeoPoint = (value: unknown): FieldGeoPoint | undefined =>
  isRecord(value) && typeof value.lat === "number" && typeof value.lng === "number"
    ? { lat: value.lat, lng: value.lng }
    : undefined;

/**
 * A `geoLocation` answer. `current` / `selected` are read point by point rather than demanded: a
 * stored answer routinely carries `selected: null` (the user captured a live position and picked
 * nothing), and `GoogleMap` defaults both props to `null` anyway, so an absent point stays an absent
 * point instead of taking the whole map off the page.
 */
const readGeoValue = (value: unknown): FieldGeoValue | undefined =>
  isRecord(value)
    ? {
        current: readGeoPoint(value.current),
        selected: readGeoPoint(value.selected),
        mapZoomLevel: readNumber(value.mapZoomLevel),
      }
    : undefined;

/**
 * Pairs one question's `fieldType` with the answer that `fieldType` implies — the single place the
 * correspondence in `AnswerValueByFieldType` is established, and it is established by LOOKING at the
 * value. An answer that is not the shape its `fieldType` implies comes back as `undefined`, which is
 * the unanswered case every branch of `getFieldAns` already renders as an empty cell; the same value
 * handed to those branches under an asserted name would have thrown there instead (`.map` / `.join`
 * on a non-array, or an object child React refuses to print).
 */
const toTypedFieldAnswer = (fieldType: QuestionField["fieldType"], value: unknown): TypedFieldAnswer => {
  switch (fieldType) {
    case "single-line":
    case "multi-line":
    case "single-select":
    case "number":
    case "autoNumber":
    case "date-time":
    case "formula":
    case "concatenate":
      return { fieldType, value: isPrintableScalar(value) ? value : undefined };
    case "multi-select":
      return { fieldType, value: readStringList(value) };
    case "look-up":
      return { fieldType, value: readLookUpList(value) };
    case "users":
      return { fieldType, value: readUserList(value) };
    case "fileUpload":
    case "image":
      return { fieldType, value: readFileList(value) };
    case "url":
      return { fieldType, value: readUrlValue(value) };
    case "geoLocation":
      return { fieldType, value: readGeoValue(value) };
    default:
      return { fieldType, value };
  }
};

/** Every answer shape the renderers below handle, as a single union for the read-only view. */
export type FieldAnswerValue = AnswerValueByFieldType[keyof AnswerValueByFieldType] | null;

/**
 * The captured answer for one question. `createdBy` / `createdDate` come from the wire schema;
 * `value` is parameterised because its runtime shape is chosen by `eachField.fieldType`.
 */
export type FieldAnswer<TValue = unknown> = NonNullable<QuestionField["answer"]> & { value?: TValue };

/**
 * The subset of `useFormValidator`'s return value the renderers read. Declared structurally so the
 * redux-held validator and a locally created one are both accepted.
 */
type FieldFormValidator = {
  error?: Record<string, string>;
  validate?: (args: { name: string; value: unknown }) => boolean;
};

/**
 * The form instance as the renderers forward it; only `_id` and the template are ever read. The
 * sections are `Partial` so both a live instance (whose `sectionHeading` is optional on the wire)
 * and a strict authored template satisfy it.
 */
type RendererFormData = { _id?: string; selectedForm?: { template?: Array<Partial<FormTemplateField>> } };

/**
 * Payload handed back to `useForms`' `removeUserFromList`. Mirrors the interface of the same name
 * in userCard.tsx (file-local there, so it cannot be imported), widened where this call site may
 * legitimately have nothing to send — the read-only answer view builds its props by hand.
 */
export type RemoveUserFromListArgs = {
  fieldIndex: number | null | undefined;
  sectionIndex: number | null | undefined;
  user: Partial<UserTagProfileData>;
  section: Partial<FormTemplateField> | undefined;
  eachField: QuestionField;
  source: string | undefined;
  tableRowIndex: number | undefined;
  /** Forwarded untouched to the hook, which decides what to do with them. */
  handleCustomFieldData: unknown;
  extraParams: unknown;
};

/** Payload for the "add users to this question" modal opener. */
export type OpenAddUserModalArgs = {
  eachField: RenderableQuestionField;
  fieldIndex: number | null;
  sectionIndex: number | null;
  section: Partial<FormTemplateField>;
  source: string;
  tableRowIndex: number;
  handleCustomFieldData: unknown;
  extraParams: unknown;
  tableRowId: string | number | null;
};

/** Payload for a local-device file/image pick on a `fileUpload` / `image` question. */
export type FileUploadChangeArgs = {
  e: React.ChangeEvent<HTMLInputElement>;
  sectionId: string;
  eachField: RenderableQuestionField;
  sectionIndex: number | null;
  fieldIndex: number | null;
  source: string;
  tableRowIndex: number;
  tableRowId: string | number | null;
  allowedFileTypes: QuestionField["allowedFileTypes"];
  formData: RendererFormData;
  handleCustomFieldData: unknown;
  extraParams: unknown;
};

/** Payload for removing one already-uploaded file/image from an answer. */
export type RemoveUploadedFileArgs = {
  fileData: FieldFileValue;
  eachField: RenderableQuestionField;
  sectionIndex: number;
  fieldIndex: number;
  sectionId: string;
  questionId: string;
  tableRowIndex: number;
  source: string;
  handleCustomFieldData: unknown;
  currentAnswer: FieldAnswer<FieldFileValue[]>;
  /** Absent on the image-editor delete path, which never sent it. */
  extraParams?: unknown;
};

/**
 * Payload for clearing every file from one answer at once — `RemoveUploadedFileArgs` without the entry
 * to remove, since the target is all of them. The handler reads the file list off the stored answer
 * rather than taking it from here, so that the positions it sends the server match what the server has.
 */
export type RemoveAllUploadedFilesArgs = Omit<RemoveUploadedFileArgs, "fileData">;

/**
 * Payload for opening the URL / geo-location editor modals. `state` toggles visibility and is the
 * only required member: the renderers below open a modal with the full payload, but the modals
 * themselves close again by calling the same handler with a bare `{ state: false }`.
 */
export type FieldModalStateArgs = {
  state: boolean;
  sectionIndex?: number | null;
  eachField?: RenderableQuestionField;
  tableRowIndex?: number;
  answer?: FieldAnswer<unknown>;
  fieldIndex?: number | null;
  sectionId?: string;
  source?: string;
  tableRowId?: string | number | null;
};

/** Payload for persisting an image edited in place. */
export type SaveEditedImageArgs = {
  eachField: RenderableQuestionField;
  fieldIndex: number | null;
  sectionIndex: number | null;
  tableRowIndex: number;
  source: string;
  imageIndex: number;
  base64EncodedImg: string;
  handleCustomFieldData: unknown;
  currentAnswer: FieldAnswer<FieldFileValue[]>;
  extraParams: unknown;
  awsKey: string | undefined;
  sectionId: string;
  questionId: string;
};

export type QuestionFieldRenderProps<TValue = unknown> = {
  eachField: RenderableQuestionField;
  currentAnswer: FieldAnswer<TValue>;
  enabled: boolean;
  name: string;
  source: string;
  width?: string;
  formValidator: FieldFormValidator;
  section: Partial<FormTemplateField>;
  fieldIndex: number;
  sectionIndex: number;
  sectionId: string;
  questionId: string;
  tableRowIndex: number;
  tableRowId: string | number | null;
  formData: RendererFormData;
  extraParams: Record<string, unknown>;
  spaceId?: string;
  projectId?: string;
  multiTextRows?: number;
  formulaValue?: number | null;
  concatenatedValue?: string | null;
  locationData: Array<{ id: string; label: string; parentId?: string | null }>;
  teamsData: Array<{ id: string; name: string }>;
  /**
   * Optional because that is how it really arrives. `FormQuestionFieldsProps.isFileUploading`
   * (questionFields.tsx) declares it `?:`, and the props object reaches these renderers through the
   * `as QuestionFieldRenderProps<never>` assertion in `getFieldAnsComponent` — an assertion cannot
   * add a value the caller never passed, so declaring it required here was the type lying about a
   * prop the component's own published props leave out. Every read below is guarded accordingly.
   */
  isFileUploading?: Array<{ type?: string; uniqueId: string | number }>;
  /** Forwarded untouched to `useForms`; this file never invokes it. */
  handleCustomFieldData: unknown;
  handleOnChange: (args?: unknown) => void;
  handleOnBlur: (args?: unknown) => void;
  handleRemoveLocation: () => void;
  handleFileUploadClick: () => void;
  /**
   * Opens the image-capture flow (camera / browse chooser; camera straight away on
   * `allowCameraCaptureOnly` fields). Optional because `formToPrint` builds partial props by hand.
   */
  openImageUploadOptions?: () => void;
  handleUrlFieldClick: () => void;
  handleFileUploadChange: (args: FileUploadChangeArgs) => void;
  handleRemoveUploadedFile: (args: RemoveUploadedFileArgs) => void;
  /**
   * The two bulk actions on a file/image answer, both implemented in `useQuestionFields` so mobile gets
   * them for free. Neither takes arguments: that hook is per-field, so it already knows which answer it
   * is acting on and assembles the removal payload itself.
   */
  handleDownloadAllFiles: () => void;
  handleDeleteAllFilesClick: () => void;
  /** True while a bulk download is in flight, so the button can show it is busy. */
  isDownloadingAllFiles: boolean;
  handleURLModalState: (args: FieldModalStateArgs) => void;
  handleGeoLocationModalState: (args: FieldModalStateArgs) => void;
  removeUserFromList: (args: RemoveUserFromListArgs) => void;
  openAddUserModal: (args: OpenAddUserModalArgs) => void;
  saveEditedImage: (args: SaveEditedImageArgs) => void;
};

/**
 * What the read-only answer view (`getFieldAns`) is given. Everything is optional because
 * `formToPrint` builds this object by hand out of five fields — none of the live renderer's
 * handlers exist on that path.
 */
export type FieldAnswerViewProps = Partial<QuestionFieldRenderProps<unknown>> & {
  eachField: RenderableQuestionField;
};

export const getMapWarnings = (warningMessage = "") => (
  <div className={`h-12 bg-system_colour_12 w-fit ps-3 gap-2 bg-opacity-30 pe-3 rounded-lg flex items-center`}>
    <span
      className={`w-5 h-5 flex items-center justify-center rounded-full text-white text-xs cursor-default bg-yellow-400`}
    >
      !
    </span>
    <span>{warningMessage}</span>
  </div>
);

export const getOverDistanceWarning = () => getMapWarnings(t("Selected location differs from captured location"));

export const getNoPermissionWarning = () =>
  getMapWarnings(t("Live location not captured since necessary permission not given"));

export const getUserCardAndChip = ({ user, p }: { user: FieldUserValue; p: FieldAnswerViewProps }) => {
  return p.eachField.viewType === optionsViewType.CHIP ? (
    <UserNameTagComponent
      id={user._id}
      // `removeUserFromList!` below is erased at emit. It is absent only on the read-only print
      // path, which always arrives with `enabled: false`, so this arrow is not built there.
      // The disabled arm is `undefined`, not `null`: `handleDeleteIconClicked` is declared
      // `?: () => void`, and it reaches MUI's `Chip` as `onDelete`, which only tests it for
      // truthiness — so the two are the same to the chip, and only one of them is the declared type.
      // NOTE: this comment sits above the attribute, not inside the ternary. Placed after the `?`,
      // it makes esbuild parenthesise the arrow, and the emitted JS then differs by two bytes.
      handleDeleteIconClicked={
        p.enabled
          ? () =>
              p.removeUserFromList!({
                fieldIndex: p.fieldIndex,
                sectionIndex: p.sectionIndex,
                user,
                eachField: p.eachField,
                section: p.section,
                source: p.source,
                tableRowIndex: p.tableRowIndex,
                handleCustomFieldData: p.handleCustomFieldData,
                extraParams: p.extraParams,
              })
          : undefined
      }
    />
  ) : (
    <UserCard
      fieldIndex={p.fieldIndex}
      sectionIndex={p.sectionIndex}
      section={p.section}
      source={p.source}
      tableRowIndex={p.tableRowIndex}
      showAnswersOnly={!p.enabled}
      handleCustomFieldData={p.handleCustomFieldData}
      extraParams={p.extraParams}
      removeUserFromList={p.removeUserFromList}
      field={p.eachField}
      id={user._id}
    />
  );
};

export const renderSingleLine = (p: QuestionFieldRenderProps<string>) => (
  <TextFieldComponent
    defaultValue={p.currentAnswer.value ?? null}
    onChange={p.handleOnChange}
    onBlur={p.handleOnBlur}
    disabled={!p.enabled}
    size="small"
    key={p.name}
    error={!!_.get(p.formValidator, `error.${p.name}`)}
    placeholder={p.eachField.placeholderText ?? ""}
    width={p.width}
  />
);

export const renderMultiLine = (p: QuestionFieldRenderProps<string>) => (
  <textarea
    style={{
      border: "1px solid #19204880",
      outline: "none",
      width: p.source === formSectionType.FORM ? "80%" : "100%",
      padding: "0.42rem 0 0.42rem 0.5rem",
      height: "100%",
      color: "black",
    }}
    disabled={!p.enabled}
    rows={p.multiTextRows || 5}
    key={p.name}
    name={p.name}
    placeholder={p.eachField.placeholderText ?? ""}
    defaultValue={p.currentAnswer.value ?? ""}
    onBlur={p.handleOnBlur}
    onChange={p.handleOnChange}
  />
);

export const renderSingleSelect = (p: QuestionFieldRenderProps<string>) => {
  if (p.eachField.viewType === optionsViewType.LIST) {
    return (
      <RadioGroup
        value={p.currentAnswer.value ?? undefined}
        defaultValue={p.eachField.defaultValue}
        onChange={(args) => {
          p.handleOnBlur(args);
          p.handleOnChange(args);
        }}
      >
        {p.eachField.answerChoices?.map((choice: string) => (
          <FormControlLabel
            key={choice}
            disabled={!p.enabled}
            value={choice}
            control={<RadioButton />}
            label={choice}
          />
        ))}
      </RadioGroup>
    );
  }

  return (
    <AutoComplete
      disabled={!p.enabled}
      value={p.currentAnswer.value ?? undefined}
      defaultValue={p.eachField.defaultValue}
      onChange={(_, newValue) => {
        p.handleOnChange({ target: { value: newValue } });
        p.handleOnBlur({ target: { value: newValue } });
      }}
      id={p.name}
      options={p.eachField.answerChoices}
      getOptionLabel={(opt) => opt}
      sx={{ width: p.width }}
      renderInput={(params) => (
        <TextFieldComponent
          {...params}
          placeholder={p.eachField.placeholderText ?? ""}
          error={!!_.get(p.formValidator, `error.${p.name}`)}
        />
      )}
    />
  );
};

export const renderMultiSelect = (p: QuestionFieldRenderProps<string[]>) => {
  if (p.eachField.viewType === optionsViewType.LIST) {
    return (
      <FormGroup>
        {p.eachField.answerChoices?.map((choice: string) => (
          <FormControlLabel
            key={choice}
            control={
              <CheckBoxComponent
                onChange={(args) => {
                  p.handleOnBlur(args);
                  p.handleOnChange(args);
                }}
                disabled={!p.enabled}
                name={choice}
                checked={Array.isArray(p.currentAnswer.value) && p.currentAnswer.value.includes(choice)}
              />
            }
            label={choice}
          />
        ))}
      </FormGroup>
    );
  }

  return (
    <AutoComplete
      disabled={!p.enabled}
      multiple
      key={p.name}
      disableCloseOnSelect
      value={p.currentAnswer.value ?? undefined}
      defaultValue={[]}
      getOptionLabel={(opt) => opt}
      onChange={(_, newValue) => {
        p.handleOnChange({ target: { value: newValue } });
        p.handleOnBlur({ target: { value: newValue } });
      }}
      id={p.name}
      options={p.eachField.answerChoices}
      sx={{ width: p.width }}
      renderInput={(params) => (
        <TextFieldComponent
          {...params}
          placeholder={p.eachField.placeholderText ?? ""}
          error={!!_.get(p.formValidator, `error.${p.name}`)}
        />
      )}
    />
  );
};

const currentDateForDateField = (p: QuestionFieldRenderProps<number>) =>
  p.enabled ? (
    <Box
      className={`${
        p.currentAnswer.value ? "cursor-not-allowed pointer-events-none" : "cursor-pointer"
      } border border-slate-400 ${p.source === formSectionType.FORM ? "w-1/2" : "w-full"} h-8 flex items-center p-2`}
      onClick={(args) => {
        p.handleOnBlur(args);
        p.handleOnChange(args);
      }}
    >
      {p.currentAnswer.value ? (
        convertDate(Number(p.currentAnswer.value), "dateForTooltip")
      ) : (
        <Text className="text-xs text-slate-500">{t("Click here to set date")}</Text>
      )}
    </Box>
  ) : (
    <div
      className={`cursor-not-allowed pointer-events-none border bg-gray-100 border-slate-400 ${p.source === formSectionType.FORM ? "w-1/2" : "w-full"} h-8 flex items-center p-2`}
    >
      {p.currentAnswer.value && convertDate(Number(p.currentAnswer.value), "dateForTooltip")}
    </div>
  );
export const renderDateTime = (p: QuestionFieldRenderProps<number>) => {
  // Epoch ms, as the form-template builder stores a date question's range. `|| undefined` where it
  // was `|| null` before: both are ignored by the two widgets below, and only one of them is a
  // value `minDate` / `maxDate` accept.
  const min = p.eachField.minFieldRange || undefined;
  const max = p.eachField.maxFieldRange || undefined;

  // The bounds the question's `selectionScope` puts on the picker, typed as what is really being
  // built instead of laundered through `as Record<string, never>` so that the spread below is
  // checked against the widget's props. `rangeBased` REPLACES the disable flags rather than adding
  // to them, which is what the original chain did by reassigning the whole object; only one scope
  // can match anyway. Bounds stay epoch ms here because `DateTimeComponent` declares
  // `minDate`/`maxDate` as `Date | string | number` and converts them itself — the raw MUI
  // `DatePicker` on the other branch does not, and converts at the call site.
  let extraProps: { disableFuture?: boolean; disablePast?: boolean; minDate?: number; maxDate?: number } = {};
  if (p.eachField.selectionScope === "pastDateTime") extraProps.disableFuture = true;
  if (p.eachField.selectionScope === "futureDateTime") extraProps.disablePast = !p.currentAnswer.value;
  if (p.eachField.selectionScope === "rangeBased") extraProps = { minDate: min, maxDate: max };

  if (p.eachField.selectionScope === "currentDateTime") {
    return currentDateForDateField(p);
  }

  if (p.eachField.displayAs === "dateAndTime") {
    return (
      <DateTimeComponent
        slotProps={{ field: { clearable: true, onClear: () => p.handleOnBlur(null) } }}
        {...extraProps}
        disabled={!p.enabled}
        key={p.name}
        sx={{ width: p.width }}
        format="DD/MM/YYYY hh:mm A"
        error={!!_.get(p.formValidator, `error.${p.name}`)}
        defaultValue={p.currentAnswer.value || undefined}
        placeholder={p.eachField.placeholderText ?? ""}
        value={p.currentAnswer.value ?? undefined}
        onChange={p.handleOnChange}
        onAccept={p.handleOnBlur}
      />
    );
  } else {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          sx={{
            width: p.width ?? "100%",
            "&.MuiFormControl-root .MuiInputBase-root": {
              height: "2rem",
              background: "white",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--info-light-blue)",
              borderWidth: "0.0625rem",
            },
          }}
          slotProps={{ field: { clearable: true, onClear: () => p.handleOnBlur(null) } }}
          disableFuture={extraProps.disableFuture}
          disablePast={extraProps.disablePast}
          // FIXED: these two used to arrive here as the raw epoch numbers `extraProps` carries,
          // which `DatePicker` cannot use — `minDate`/`maxDate` are Dayjs on the raw MUI picker, so
          // a `rangeBased` date-ONLY question was not range-limited in the widget at all. The
          // date-AND-time branch above has always worked, because `DateTimeComponent` does this
          // same `dayjs()` conversion for us; the two branches now agree.
          minDate={extraProps.minDate ? dayjs(extraProps.minDate) : undefined}
          maxDate={extraProps.maxDate ? dayjs(extraProps.maxDate) : undefined}
          disabled={!p.enabled}
          format="DD/MM/YYYY"
          // KNOWN BUG, preserved verbatim: `error` is not a prop on MUI's `DatePicker` (v6 moved it
          // to `slotProps.textField.error`), so the red validation outline has never rendered on a
          // date-ONLY question — only on the date-and-time branch above, which goes through our own
          // `DateTimeComponent` wrapper. This was invisible until now because the sibling
          // `{...extraProps}` spread was `any`, and an `any` spread switches off prop checking for
          // the whole JSX element. Suppressed rather than fixed: moving it under `slotProps` would
          // start rendering an outline that users do not currently see, which is a behaviour change
          // and out of scope for a typing pass. The suppression is self-checking — it fails the
          // build if the prop ever becomes valid.
          // @ts-expect-error -- invalid prop kept deliberately; see above.
          error={!!_.get(p.formValidator, `error.${p.name}`)}
          placeholder={p.eachField.placeholderText ?? ""}
          value={p.currentAnswer.value ? dayjs(p.currentAnswer.value) : null}
          onChange={p.handleOnChange}
          onAccept={p.handleOnBlur}
        />
      </LocalizationProvider>
    );
  }
};

const LocationTreeSelect = (p: QuestionFieldRenderProps<FieldLookUpValue[]>) => {
  const locationData = Array.isArray(p.locationData) ? p.locationData : [];

  const selectedLabel = (() => {
    // The `a.id ?? a` this replaces was not belt-and-braces: a Location answer stores `{ id, name }`
    // entries today (see `getLookUpAnswer` in questionFields.tsx), but older ones stored the id on
    // its own, and this read has to match both. That is a wider element type than the write path
    // produces, so it is stated here rather than in the exported `FieldLookUpValue`.
    const entries: Array<FieldLookUpValue | string> = Array.isArray(p.currentAnswer.value) ? p.currentAnswer.value : [];
    const ids = entries.map((a) => (typeof a === "string" ? a : a.id));
    const found = locationData.find((d) => ids.includes(d.id));
    return found?.label ?? "";
  })();

  return (
    <div>
      <TreeSelectDropDown
        disabled={!p.enabled}
        key={p.name}
        disabledParentSelection={true}
        placeholder={p.eachField.placeholderText ?? ""}
        treeData={locationData}
        value={selectedLabel}
        onSelectionChange={(params: TreeDropDownNode) => {
          const args = { itemData: { _id: params.id, name: params.label, ...params } };
          p.handleOnChange(args);
          p.handleOnBlur(args);
        }}
      />
    </div>
  );
};

export const renderLookUp = (p: QuestionFieldRenderProps<FieldLookUpValue[]>) => {
  if (p.eachField.lookUpAnsFieldType === "Location") {
    return <LocationTreeSelect {...p} />;
  }

  if (p.eachField.lookUpAnsFieldType === "Teams") {
    return (
      <AutoComplete
        disabled={!p.enabled}
        multiple
        key={p.name}
        disableCloseOnSelect
        getOptionLabel={(opt) => opt.name}
        value={p.currentAnswer.value ?? []}
        onChange={(_, newValue) => {
          p.handleOnChange(newValue);
          p.handleOnBlur(newValue);
        }}
        id={p.name}
        options={p.teamsData}
        sx={{ width: p.width }}
        renderInput={(params) => (
          <TextFieldComponent
            {...params}
            placeholder={p.eachField.placeholderText ?? ""}
            error={!!_.get(p.formValidator, `error.${p.name}`)}
          />
        )}
      />
    );
  }

  return <></>;
};

export const renderNumber = (p: QuestionFieldRenderProps<number>) => (
  <TextFieldComponent
    defaultValue={p.currentAnswer.value ?? ""}
    onChange={p.handleOnChange}
    onBlur={p.handleOnBlur}
    error={!!_.get(p.formValidator, `error.${p.name}`)}
    slotProps={{
      htmlInput: {
        min: p.eachField.minFieldRange ? +p.eachField.minFieldRange : 0,
        max: p.eachField.maxFieldRange ? +p.eachField.maxFieldRange : null,
      },
    }}
    key={p.name}
    disabled={!p.enabled}
    placeholder={p.eachField.placeholderText ?? ""}
    width={p.width}
    type="number"
  />
);

export const renderUsers = (p: QuestionFieldRenderProps<FieldUserValue[]>) => (
  <div>
    <Box
      onClick={() =>
        p.enabled
          ? p.openAddUserModal({
              eachField: p.eachField,
              fieldIndex: p.fieldIndex,
              sectionIndex: p.sectionIndex,
              section: p.section,
              source: p.source,
              tableRowIndex: p.tableRowIndex,
              handleCustomFieldData: p.handleCustomFieldData,
              extraParams: p.extraParams,
              tableRowId: p.tableRowId,
            })
          : () => {}
      }
      className="bg-button_light h-8 w-14 rounded-3xl justify-center flex cursor-pointer items-center"
    >
      <IconComponent className={p.enabled ? "" : "opacity-50"} name="UserProfile" width="1.5rem" height="1.5rem" />
    </Box>
    <div className="flex flex-wrap gap-5 overflow-auto pt-5">
      {p.currentAnswer.value?.map((user) => getUserCardAndChip({ user, p }))}
    </div>
  </div>
);

export const renderFileUpload = (p: QuestionFieldRenderProps<FieldFileValue[]>) => (
  <div className="flex h-full w-full overflow-auto flex-col gap-5">
    <span className="flex flex-wrap items-center gap-2">
      <input
        accept={(p.eachField.allowedFileTypes ?? []).join(",")}
        type="file"
        id={`${p.name}`}
        onChange={(e) =>
          p.handleFileUploadChange({
            e,
            sectionId: p.sectionId,
            eachField: p.eachField,
            sectionIndex: p.sectionIndex,
            fieldIndex: p.fieldIndex,
            source: p.source,
            tableRowIndex: p.tableRowIndex,
            tableRowId: p.tableRowId,
            allowedFileTypes: p.eachField.allowedFileTypes,
            formData: p.formData,
            handleCustomFieldData: p.handleCustomFieldData,
            extraParams: p.extraParams,
          })
        }
        style={{ display: "none" }}
      />
      <ButtonComponent
        onClick={p.handleFileUploadClick}
        disabled={!p.enabled}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <PlusIcon color={p.enabled ? "var(--primary-text)" : "var(--system-colour-37"} />
          <span>{p.eachField.placeholderText}</span>
        </span>
      </ButtonComponent>
      {/* The same two bulk actions `renderImage` offers: the handlers come from `useQuestionFields`,
          which is per-field and field-type agnostic, so a document answer gets them on the same terms
          as an image one. Download All skips entries with no `awsKey` (File Manager links own no object
          of their own) and says how many it left out. */}
      {!!p.currentAnswer.value?.length && (
        <>
          <ButtonComponent
            onClick={p.handleDownloadAllFiles}
            disabled={p.isDownloadingAllFiles}
            buttonType={BUTTON_TYPES.TEXT_BUTTON}
          >
            <span className="flex gap-1 items-center">
              <IconComponent name="download" width="1rem" height="1rem" isEnabled={!p.isDownloadingAllFiles} />
              <span>{t("Download All")}</span>
            </span>
          </ButtonComponent>
          <ButtonComponent
            onClick={p.handleDeleteAllFilesClick}
            disabled={!p.enabled}
            buttonType={BUTTON_TYPES.TEXT_BUTTON}
          >
            <span className="flex gap-1 items-center">
              <IconComponent name="delete" width="1rem" height="1rem" isEnabled={p.enabled} />
              <span>{t("Delete All")}</span>
            </span>
          </ButtonComponent>
        </>
      )}
    </span>
    <span className="flex flex-col pe-3 overflow-auto gap-4 w-full h-full">
      {p.currentAnswer.value?.map((data, index: number) => (
        <span
          key={`${data.name}-${index}`}
          className="flex gap-3 border-solid border-2 border-button_light w-fit pe-2 rounded-lg ps-2 items-center"
        >
          <IconComponent
            onClick={(e: React.MouseEvent) => handleFileOpen(e, data, p.spaceId, p.projectId)}
            name="openInViewer"
            width="1rem"
            height="1rem"
          />
          <span className="w-14 h-10 items-center flex object-contain"> {getFileIcon(data.name)}</span>
          <span>{data.name}</span>
          {p.enabled && (
            <span className="w-8">
              <IconComponent
                className="cursor-pointer"
                onClick={() =>
                  p.handleRemoveUploadedFile({
                    fileData: data,
                    eachField: p.eachField,
                    sectionIndex: p.sectionIndex,
                    fieldIndex: p.fieldIndex,
                    sectionId: p.sectionId,
                    questionId: p.questionId,
                    tableRowIndex: p.tableRowIndex,
                    source: p.source,
                    handleCustomFieldData: p.handleCustomFieldData,
                    currentAnswer: p.currentAnswer,
                    extraParams: p.extraParams,
                  })
                }
                name="cross"
                width="1rem"
                height="1rem"
              />
            </span>
          )}
        </span>
      ))}
      {p.isFileUploading?.map(
        (data) =>
          data.uniqueId == p.name && (
            <span
              key={data.uniqueId}
              className="w-32 h-32 border-button_light p-1 items-center justify-center rounded flex border-2"
            >
              <CircularProgress />
            </span>
          ),
      )}
    </span>
  </div>
);

export const renderImage = (p: QuestionFieldRenderProps<FieldFileValue[]>) => (
  <div className="flex h-full w-full overflow-auto flex-col gap-5">
    <span className="flex flex-wrap items-center gap-2">
      <input
        accept={(p.eachField.allowedFileTypes ?? []).join(",")}
        type="file"
        id={`${p.name}`}
        onChange={(e) =>
          p.handleFileUploadChange({
            e,
            sectionId: p.sectionId,
            eachField: p.eachField,
            sectionIndex: p.sectionIndex,
            fieldIndex: p.fieldIndex,
            source: p.source,
            tableRowIndex: p.tableRowIndex,
            tableRowId: p.tableRowId,
            allowedFileTypes: p.eachField.allowedFileTypes,
            formData: p.formData,
            handleCustomFieldData: p.handleCustomFieldData,
            extraParams: p.extraParams,
          })
        }
        style={{ display: "none" }}
      />
      <ButtonComponent
        onClick={() =>
          p.openImageUploadOptions ? p.openImageUploadOptions() : document.getElementById(`${p.name}`)?.click()
        }
        disabled={!p.enabled}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <IconComponent name="upload" width="1rem" height="1rem" isEnabled={p.enabled} />
          <span>{p.eachField.placeholderText}</span>
        </span>
      </ButtonComponent>
      {!!p.currentAnswer.value?.length && (
        <>
          <ButtonComponent
            onClick={p.handleDownloadAllFiles}
            disabled={p.isDownloadingAllFiles}
            buttonType={BUTTON_TYPES.TEXT_BUTTON}
          >
            <span className="flex gap-1 items-center">
              <IconComponent name="download" width="1rem" height="1rem" isEnabled={!p.isDownloadingAllFiles} />
              <span>{t("Download All")}</span>
            </span>
          </ButtonComponent>
          <ButtonComponent
            onClick={p.handleDeleteAllFilesClick}
            disabled={!p.enabled}
            buttonType={BUTTON_TYPES.TEXT_BUTTON}
          >
            <span className="flex gap-1 items-center">
              <IconComponent name="delete" width="1rem" height="1rem" isEnabled={p.enabled} />
              <span>{t("Delete All")}</span>
            </span>
          </ButtonComponent>
        </>
      )}
    </span>
    {(!!p.currentAnswer.value?.length || !!p.isFileUploading?.length) && (
      <span className="flex flex-wrap flex-row pe-3 overflow-auto gap-4 w-full h-full">
        {p.currentAnswer.value?.map((data, index: number) => (
          <span
            key={data.awsKey}
            style={{ width: `${p.eachField.displaySize ?? DEFAULT_IMAGE_DISPLAY_WIDTH}%` }}
            className="h-fit border-button_light p-1 rounded flex border-2"
          >
            <ImageEditor
              isEditable={p.enabled}
              imageData={attachmentFileUrl(data.awsKey)}
              // A filled-in answer is something a reader may want to keep a copy of, so unlike the
              // logo/profile pickers this editor offers a download — saved under the uploaded file's own
              // name rather than the storage key the URL carries.
              showDownloadButton
              fileName={data.name}
              onSubmitFunction={(base64EncodedImg: string) =>
                p.saveEditedImage({
                  eachField: p.eachField,
                  fieldIndex: p.fieldIndex,
                  sectionIndex: p.sectionIndex,
                  tableRowIndex: p.tableRowIndex,
                  source: p.source,
                  imageIndex: index,
                  base64EncodedImg,
                  handleCustomFieldData: p.handleCustomFieldData,
                  currentAnswer: p.currentAnswer,
                  extraParams: p.extraParams,
                  awsKey: data.awsKey,
                  sectionId: p.sectionId,
                  questionId: p.questionId,
                })
              }
              heading={t("Edit Image")}
              onDelete={() =>
                p.handleRemoveUploadedFile({
                  eachField: p.eachField,
                  fileData: data,
                  sectionIndex: p.sectionIndex,
                  fieldIndex: p.fieldIndex,
                  sectionId: p.sectionId,
                  questionId: p.questionId,
                  source: p.source,
                  tableRowIndex: p.tableRowIndex,
                  currentAnswer: p.currentAnswer,
                  handleCustomFieldData: p.handleCustomFieldData,
                })
              }
              defaultImageData={undefined}
            />
          </span>
        ))}
        {p.isFileUploading?.map(
          (data) =>
            data.uniqueId == p.name && (
              <span
                key={data.uniqueId}
                className="h-32 w-32 border-button_light p-1 items-center justify-center rounded flex border-2"
              >
                <CircularProgress />
              </span>
            ),
        )}
      </span>
    )}
  </div>
);

const getURLEditBtn = (p: QuestionFieldRenderProps<FieldUrlValue>) => {
  return (
    <div>
      <ButtonComponent
        disabled={!p.enabled}
        onClick={() => {
          p.handleURLModalState({
            state: true,
            sectionIndex: p.sectionIndex,
            eachField: p.eachField,
            tableRowIndex: p.tableRowIndex,
            answer: p.currentAnswer,
            fieldIndex: p.fieldIndex,
            sectionId: p.sectionId,
            source: p.source,
            tableRowId: p.tableRowId,
          });
        }}
      >
        <IconComponent
          className={`h-5 ${p.enabled ? "" : "opacity-50"}`}
          name="editProfile"
          width="1.25rem"
          height="1.25rem"
        />
      </ButtonComponent>
    </div>
  );
};
export const renderURL = (p: QuestionFieldRenderProps<FieldUrlValue>) => {
  if (p.currentAnswer.value?.displayText && p.currentAnswer.value.URL) {
    if (p.eachField.displayAs == "button") {
      return (
        <div className="flex items-center gap-3">
          <ButtonComponent onClick={p.handleUrlFieldClick} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
            <Text sx={{ maxWidth: "12.5rem" }}>{_.get(p.currentAnswer, "value.displayText", "")}</Text>
          </ButtonComponent>
          {getURLEditBtn(p)}
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-3">
          <Box
            onClick={p.handleUrlFieldClick}
            className="text-gradient_color cursor-pointer underline underline-offset-2"
          >
            {_.get(p.currentAnswer, "value.displayText", "")}
          </Box>
          {getURLEditBtn(p)}
        </div>
      );
    }
  }
  return (
    <span>
      <ButtonComponent
        disabled={!p.enabled}
        onClick={() =>
          p.handleURLModalState({
            state: true,
            sectionIndex: p.sectionIndex,
            eachField: p.eachField,
            tableRowIndex: p.tableRowIndex,
            answer: p.currentAnswer,
            fieldIndex: p.fieldIndex,
            sectionId: p.sectionId,
            source: p.source,
            tableRowId: p.tableRowId,
          })
        }
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <IconComponent name="URL" width="1.5rem" height="1.5rem" className={`h-5 ${p.enabled ? "" : "opacity-50"}`} />
          <Text>{t("Enter URL")}</Text>
        </span>
      </ButtonComponent>
    </span>
  );
};

export const renderGeoLocation = (p: QuestionFieldRenderProps<FieldGeoValue>) => {
  const distance = getDistanceInMeters({
    pos1: p.currentAnswer.value?.current,
    pos2: p.currentAnswer.value?.selected,
  });
  return p.currentAnswer.value ? (
    <div className="flex-col flex gap-5">
      <div className="flex h-80 w-120 border flex-col gap-5 border-button_light p-3 rounded-lg">
        <GoogleMap
          allowManualSelection={false}
          allowPanning={false}
          autoFetchLiveLocation={false}
          showLiveLocation={!!p.currentAnswer.value.current}
          zoomLevel={p.currentAnswer.value.mapZoomLevel}
          defaultCenter={p.currentAnswer.value.current}
          additionalMarker={p.currentAnswer.value.selected}
          allowSearch={false}
          getLocationCallBack={undefined}
        />
        {distance > 500 && getOverDistanceWarning()}
        {p.eachField.captureLiveLocation && !p.currentAnswer.value.current && getNoPermissionWarning()}
      </div>
      <div>
        <ButtonComponent disabled={!p.enabled} onClick={p.handleRemoveLocation}>
          <span className="flex gap-1 items-center">
            <IconComponent className={p.enabled ? "" : "opacity-50"} name="delete" width="1.25rem" height="1.25rem" />
            <Text>{t("Remove")}</Text>
          </span>
        </ButtonComponent>
      </div>
    </div>
  ) : (
    <ButtonComponent
      onClick={() =>
        p.handleGeoLocationModalState({
          sectionIndex: p.sectionIndex,
          eachField: p.eachField,
          tableRowIndex: p.tableRowIndex,
          answer: p.currentAnswer,
          fieldIndex: p.fieldIndex,
          sectionId: p.sectionId,
          source: p.source,
          tableRowId: p.tableRowId,
          state: true,
        })
      }
      disabled={!p.enabled}
      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
    >
      <span className="flex gap-1 items-center">
        <IconComponent className={p.enabled ? "" : "opacity-50"} name="add" width="1rem" height="1rem" />
        <span>{p.eachField.placeholderText}</span>
      </span>
    </ButtonComponent>
  );
};

const renderFormulaField = (p: QuestionFieldRenderProps) => {
  const calculatedValue = p.formulaValue ?? null;
  return (
    <div className="flex flex-col gap-2" style={{ width: p.width }}>
      <TextFieldComponent value={calculatedValue ?? ""} key={p.name} disabled={true} type="number" />
      {calculatedValue === null && (
        <div className="flex items-center gap-1 text-sm text-slate-500">
          <span className="flex items-center justify-center">
            <IconComponent className="h-5" name="details" width="1.5rem" height="1.5rem" />
          </span>
          {getCalculatedFieldHintText()}
        </div>
      )}
    </div>
  );
};

const renderConcatenateField = (p: QuestionFieldRenderProps) => {
  const previewNodes = p.concatenatedValue ?? null;
  return (
    <div className="flex flex-col gap-2" style={{ width: p.width }}>
      <TextFieldComponent value={previewNodes ?? ""} multiline key={p.name} disabled={true} type="text" />
      {previewNodes === null && (
        <div className="flex items-center gap-1 text-sm text-slate-500">
          <span className="flex items-center justify-center">
            <IconComponent className="h-5" name="details" width="1.5rem" height="1.5rem" />
          </span>
          {getCalculatedFieldHintText()}
        </div>
      )}
    </div>
  );
};
const renderAutoNumberField = (p: QuestionFieldRenderProps<string | number>) => {
  return (
    <div className="flex flex-col gap-2" style={{ width: p.width }}>
      <TextFieldComponent value={p.currentAnswer.value ?? ""} key={p.name} disabled={true} type="number" />
    </div>
  );
};
/**
 * fieldType → renderer. The value type is `never` so that every renderer, each of which declares
 * the concrete answer shape its own fieldType stores, is assignable here (parameters are
 * contravariant, and `never` is assignable to every value shape). The single caller casts its
 * props object at the invocation; see questionFields.tsx.
 */
export const fieldRenderers: Record<string, (p: QuestionFieldRenderProps<never>) => ReactNode> = {
  "single-line": renderSingleLine,
  "multi-line": renderMultiLine,
  "single-select": renderSingleSelect,
  "multi-select": renderMultiSelect,
  "date-time": renderDateTime,
  "look-up": renderLookUp,
  number: renderNumber,
  users: renderUsers,
  fileUpload: renderFileUpload,
  image: renderImage,
  url: renderURL,
  geoLocation: renderGeoLocation,
  formula: renderFormulaField,
  concatenate: renderConcatenateField,
  autoNumber: renderAutoNumberField,
};
export const handleFileOpen = (
  e: React.MouseEvent,
  data: FieldFileValue,
  spaceId: string | undefined,
  projectId: string | undefined,
) => {
  e.stopPropagation();
  const { awsKey, fileVersion } = data;
  // `getViewerUrl` early-returns `undefined` when it has no `projectId`, so this is genuinely
  // `string | undefined`; the `if (url)` guard at the bottom already handled that case.
  let url: string | undefined = "";
  if (awsKey) {
    url = attachmentFileUrl(awsKey);
  } else if (fileVersion) {
    url = getViewerUrl({
      fileVersionId: fileVersion,
      spaceId,
      projectId,
    });
  }
  if (url) {
    window.open(url, "_blank");
  }
};

export const getFieldAns = (
  props: FieldAnswerViewProps,
  currentAnswer: FieldAnswer<unknown>,
  handleUrlFieldClick: () => void,
  projectId?: string,
  // Takes no argument: it comes from `useQuestionFields`, which is per-field and already knows which
  // answer it is acting on. Optional because `formToPrint` calls this with three arguments.
  handleDownloadAllFiles?: () => void,
) => {
  const { eachField, spaceId, section } = props;
  const { fieldType, displayAs, captureLiveLocation, displaySize = DEFAULT_IMAGE_DISPLAY_WIDTH } = eachField;

  // The one place the question is paired with the answer its `fieldType` implies. Every branch below
  // then NARROWS off that discriminant, instead of each one asserting a shape of its own — which is
  // what this function used to do eight times over, with nothing checking that any two of them
  // agreed. `toTypedFieldAnswer` (top of this file) checks the pairing rather than declaring it.
  const answer = toTypedFieldAnswer(fieldType, currentAnswer.value);

  if (
    answer.fieldType == "single-select" ||
    answer.fieldType == "number" ||
    answer.fieldType == "single-line" ||
    answer.fieldType == "multi-line" ||
    answer.fieldType == "autoNumber"
  ) {
    return <div className="w-4/5 flex flex-wrap">{answer.value}</div>;
  } else if (answer.fieldType == "multi-select") {
    return answer.value?.join(",");
  } else if (answer.fieldType == "date-time") {
    if (displayAs === "dateAndTime") {
      return answer.value ? convertDate(Number(answer.value), "dateForTooltip") : "";
    } else {
      return answer.value ? convertDate(Number(answer.value), "dateForProfile") : "";
    }
  } else if (answer.fieldType == "look-up") {
    const ansDisplay = answer.value?.map((ans) => ans.name);
    return ansDisplay?.length ? ansDisplay.join(",") : "";
  } else if (answer.fieldType == "users") {
    return (
      <div className="flex flex-wrap gap-5 pt-5">
        {answer.value?.map((user) => getUserCardAndChip({ user, p: { ...props, enabled: false } }))}
      </div>
    );
  } else if (answer.fieldType == "fileUpload") {
    // Hoisted for the same reason the image branch below hoists it: read three times. `| undefined`
    // comes from the answer itself — an unanswered field stores no value at all.
    const answerValue = answer.value;
    return (
      // A fragment rather than the wrapping div the image branch uses: with no handler — the print and
      // PDF path — this has to emit exactly the list span it always did, because `formToPrint`'s
      // stylesheet resets `.form-answer-file-list` and its direct children to paginate them.
      <>
        {/* Same guard as the image branch: only where a handler was injected. */}
        {handleDownloadAllFiles && (
          <span>
            <ButtonComponent
              onClick={handleDownloadAllFiles}
              disabled={!answerValue?.length}
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
            >
              <span className="flex gap-1 items-center">
                <IconComponent name="download" width="1rem" height="1rem" isEnabled={!!answerValue?.length} />
                <span>{t("Download All")}</span>
              </span>
            </ButtonComponent>
          </span>
        )}
        <span className="form-answer-file-list flex flex-col pe-3 overflow-auto gap-4 w-full h-full">
          {answerValue?.map((data, index: number) => (
            <span
              key={`${data.name}-${index}`}
              className="flex gap-3 border-solid border-2 border-button_light w-fit pe-2 rounded-lg ps-2 items-center"
            >
              <IconComponent
                onClick={(e: React.MouseEvent) => handleFileOpen(e, data, spaceId, projectId)}
                name="openInViewer"
                width="1.25rem"
                height="1.25rem"
              />
              <span className="w-14 h-10 flex items-center object-contain">{getFileIcon(data.name)}</span>
              <span>{data.name}</span>
            </span>
          ))}
        </span>
      </>
    );
  } else if (answer.fieldType == "image") {
    const answerValue = answer.value;
    return (
      <div className="flex flex-col gap-5 w-full h-full">
        {/* Rendered only where a handler was injected. `formToPrint` calls `getFieldAns` with three
            arguments, so the print and PDF views would otherwise carry an enabled button that does
            nothing — a click there has no handler to reach. */}
        {handleDownloadAllFiles && (
          <span>
            <ButtonComponent
              onClick={handleDownloadAllFiles}
              disabled={!answerValue?.length}
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
            >
              <span className="flex gap-1 items-center">
                <IconComponent name="download" width="1rem" height="1rem" isEnabled={!!answerValue?.length} />
                <span>{t("Download All")}</span>
              </span>
            </ButtonComponent>
          </span>
        )}
        <div className="form-answer-image-wrap flex flex-wrap flex-row pe-3 overflow-auto gap-4 w-full h-full">
          {answerValue?.map((data) => (
            <span
              key={data.awsKey}
              style={{ width: `${displaySize}%` }}
              className="h-fit border-button_light p-1 rounded flex border-2"
            >
              <img src={attachmentFileUrl(data.awsKey)} alt="logo" width="100%" className="form-answer-image" />
            </span>
          ))}
        </div>
      </div>
    );
  } else if (answer.fieldType == "url") {
    // Read off the narrowed answer rather than through `_.get(currentAnswer, "value.displayText", "")`.
    // Lodash's three-argument overload types its result as the default's type, so that read was the
    // literal `""` to the compiler and `if (!…)` was a branch that could never be false.
    const displayText = answer.value?.displayText;
    if (!displayText) {
      return "";
    }
    if (displayAs == "button") {
      return (
        <div>
          <ButtonComponent onClick={handleUrlFieldClick} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
            <Text sx={{ maxWidth: "12.5rem" }}>{displayText}</Text>
          </ButtonComponent>
        </div>
      );
    } else {
      return (
        <Link
          href={getUrlFromField(answer.value?.URL ?? "")}
          target="_blank"
          underline="always"
          className="cursor-pointer"
        >
          {displayText}
        </Link>
      );
    }
  } else if (answer.fieldType == "geoLocation") {
    const distance = getDistanceInMeters({
      pos1: answer.value?.current,
      pos2: answer.value?.selected,
    });
    return answer.value ? (
      <div className="flex h-80 w-120 border flex-col gap-5 border-button_light p-3 rounded-lg">
        <GoogleMap
          allowManualSelection={false}
          allowPanning={false}
          autoFetchLiveLocation={false}
          showLiveLocation={!!answer.value.current}
          zoomLevel={answer.value.mapZoomLevel}
          defaultCenter={answer.value.current}
          additionalMarker={answer.value.selected}
          allowSearch={false}
          getLocationCallBack={undefined}
        />
        {distance > 500 && getOverDistanceWarning()}
        {captureLiveLocation && !answer.value.current && getNoPermissionWarning()}
      </div>
    ) : (
      ""
    );
  } else if (answer.fieldType == "formula") {
    return getFormulaFieldValue({
      eachField,
      section,
      tableRowIndex: props.tableRowIndex,
      formData: props.formData,
    });
  } else if (answer.fieldType == "concatenate") {
    return (
      getConcatenateFieldValue({
        eachField,
        section,
        tableRowIndex: props.tableRowIndex,
        formData: props.formData,
      }) ?? ""
    );
  }
  return "";
};
